import { useCallback, useState } from "react";

const KEY = "admin_table_density";

/**
 * Row density for the list screens, remembered across visits.
 *
 * One setting for every table rather than one per screen: an admin who wants
 * dense rows wants them on stores AND sellers, and a per-screen memory means
 * setting it again on each.
 *
 * Reads are wrapped because storage throws in a private window and comes back
 * empty after a clear — neither is a reason for a list not to render.
 */
export function readDensity(): boolean {
  try {
    return localStorage.getItem(KEY) === "compact";
  } catch {
    return false;
  }
}

/**
 * `[compact, toggle]`. Comfortable (44px) is the DEFAULT and compact (34px) is
 * the opt-in, which is the design's own `compact ?? false`. Defaulting the
 * other way round inverted the control: from a standing start the button made
 * rows taller, so it read as doing nothing — or as doing the opposite.
 */
export function useTableDensity(): [boolean, () => void, (compact: boolean) => void] {
  const [compact, setCompact] = useState(readDensity);

  const toggle = useCallback(() => {
    setCompact((wasCompact) => {
      const next = !wasCompact;
      try {
        localStorage.setItem(KEY, next ? "compact" : "comfortable");
      } catch {
        // A density that cannot be remembered is still worth applying now.
      }
      return next;
    });
  }, []);

  const set = useCallback((next: boolean) => {
    setCompact(next);
    try {
      localStorage.setItem(KEY, next ? "compact" : "comfortable");
    } catch {
      // As above: a density that cannot be remembered still applies now.
    }
  }, []);

  return [compact, toggle, set];
}
